import moment from 'moment';

import i18n from '@/i18n';
import api from '@/api';

const estimateFields = [
  'costs',
  'customer',
  'spec',
  'multipliers',
  'hourly_rate',
  'cleaners_amount',
  'revised_hourly_rate',
  'area',
  'cleaning_date',
  'cleaning_time',
  'payment_method',
  'entrance_method',
  'settingsId',
];

const getName = (firstName, lastName) => {
  if (!firstName && !lastName) return i18n.t('common.unknown');
  if (firstName && lastName) return `${firstName} ${lastName}`;
  return firstName || lastName;
};

export default {
  namespaced: true,
  state: {
    current: {
      loaded: false,
      data: {},
    },
    all: {
      loaded: false,
      data: [],
    },
    settings: {
      loaded: false,
      data: {},
    },
    historicalSettings: {
      loaded: false,
      data: {},
    },
  },
  getters: {
    allEstimatesStubs(state) {
      const { all: { data } } = state;

      const compareTimestamps = (a, b) => data[b].timestamp.seconds - data[a].timestamp.seconds;
      const formatStub = (id) => {
        const { customer = {}, timestamp } = data[id];
        const { first_name, last_name, address } = customer;

        return {
          id,
          name: getName(first_name, last_name),
          address: address || '-',
          date: moment(timestamp.seconds * 1000).format(i18n.t('dateFormat.long')),
        };
      };

      return Object.keys(data).sort(compareTimestamps).map(formatStub);
    },
  },
  actions: {
    async createEstimate({ commit }, payload) {
      return api.estimate.create(payload.normalizeFormData)
        .then((costs) => {
          commit('createEstimate', { costs, ...payload.values });
        });
    },
    getAll({ commit }) {
      return api.estimate.getAll().then((estimates) => {
        commit('getAll', estimates);
      });
    },
    async loadEstimate({ commit, state }, payload) {
      if (state.all.loaded) {
        commit('copyToCurrent', payload);
      } else {
        const estimate = await api.estimate.get(payload.id);
        commit('createEstimate', estimate);
      }
    },
    deleteEstimate({ commit }, payload) {
      return api.estimate.delete(payload.id).then(() => {
        commit('deleteEstimate', payload);
      });
    },
    getSettings({ commit }) {
      return api.estimate.getSettings().then((settings) => {
        commit('getSettings', settings);
      });
    },
    async getSettingsForEstimation({ commit }, payload) {
      return api.estimate.getSettingsForEstimate(payload.id)
        .then((settings) => { commit('getSettingsForEstimation', settings); });
    },
    updateSettings({ dispatch }, payload) {
      /**
       * We refetch settings after update, because firebase returns void
       * when updating stuff in DB.
       */
      return api.estimate.updateSettings(payload).then(() => dispatch('getSettings'));
    },
  },
  mutations: {
    createEstimate(state, payload) {
      state.current.loaded = true;
      estimateFields.forEach((field) => {
        if (payload[field]) {
          state.current.data[field] = payload[field];
        }
      });
    },
    clearEstimate(state) {
      state.current.loaded = false;
      state.current.data = {};
    },
    getAll(state, payload) {
      state.all.loaded = true;
      state.all.data = payload;
    },
    copyToCurrent(state, payload) {
      state.current.loaded = true;
      state.current.data = state.all.data[payload.id];
    },
    deleteEstimate(state, payload) {
      const { id } = payload;
      const dataCopy = { ...state.all.data };
      delete dataCopy[id];
      state.all.data = dataCopy;
    },
    getSettings(state, payload) {
      state.settings.loaded = true;
      state.settings.data = payload;
    },
    clearState(state) {
      state.current = { loaded: false, data: {} };
      state.all = { loaded: false, data: {} };
      state.settings = { loaded: false, data: {} };
      state.historicalSettings = { loaded: false, data: {} };
    },
    getSettingsForEstimation(state, payload) {
      state.historicalSettings = {
        loaded: true,
        data: payload,
      };
    },
  },
};
